import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Radar,
  CheckCircle2,
  Loader2,
  Lock,
  ArrowRight,
  Shield,
  Zap,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { fetchPlans, startCheckout } from "@/lib/api";

const money = (cents, ccy) => {
  if (typeof cents !== "number") return "—";
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
};

/**
 * The paywall is what a logged-in-but-unpaid user sees when they hit /feed
 * (or any other paywalled route). Copy adapts to `subscription_status`:
 *   - none       → "Start your 14-day free trial" (first-time)
 *   - past_due   → "We couldn't charge your card — update payment"
 *   - canceled   → "Your subscription ended — resubscribe"
 *   - incomplete → "Finish setting up your subscription"
 */
function useStatusCopy(status) {
  switch (status) {
    case "past_due":
      return {
        eyebrow: "Payment failed",
        title: "We couldn't charge your card.",
        body: "Update your payment method to restore access. Your account and history are safe.",
        ctaMonthly: "Update payment (monthly)",
        ctaYearly: "Update payment (yearly)",
      };
    case "canceled":
      return {
        eyebrow: "Subscription ended",
        title: "Welcome back.",
        body: "Your previous subscription ended. Resubscribe to pick up where you left off.",
        ctaMonthly: "Resubscribe monthly",
        ctaYearly: "Resubscribe yearly",
      };
    case "incomplete":
      return {
        eyebrow: "Almost there",
        title: "Finish setting up your subscription.",
        body: "It looks like your last checkout didn't complete. Restart it below.",
        ctaMonthly: "Finish setup (monthly)",
        ctaYearly: "Finish setup (yearly)",
      };
    default:
      return {
        eyebrow: "Start your 14-day trial",
        title: "Unlock convergence intel.",
        body: "Card required to start the trial. Cancel anytime in the first 14 days and you won't be charged.",
        ctaMonthly: "Start free trial (monthly)",
        ctaYearly: "Start free trial (yearly)",
      };
  }
}

function PlanCard({ plan, featured, ctaLabel, onCheckout, loading }) {
  const perLabel = plan.interval === "year" ? "/year" : "/month";
  const savings = plan.interval === "year" ? "Save 17% · two months free" : null;
  return (
    <div
      data-testid={`paywall-plan-${plan.lookup_key}`}
      className={`flex flex-col gap-5 border p-5 ${
        featured
          ? "border-[#3b82f6] bg-[#0f172a] shadow-[0_0_40px_rgba(59,130,246,0.08)]"
          : "border-[#1e293b] bg-[#0f172a]"
      }`}
    >
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#64748b]">
          {plan.interval === "year" ? "Annual" : "Monthly"}
        </div>
        <div className="mt-2 flex items-baseline gap-1">
          <span className="text-3xl font-bold tracking-tight text-[#f8fafc]">
            {money(plan.amount, plan.currency)}
          </span>
          <span className="text-sm text-[#64748b]">
            {plan.amount % 100 === 0 ? "" : ""}{perLabel}
          </span>
        </div>
        {savings && <div className="mt-1 text-xs text-[#10b981]">{savings}</div>}
        <div className="mt-2 text-[11px] text-[#94a3b8]">
          14-day free trial · Cancel anytime · Card required
        </div>
      </div>
      <button
        type="button"
        onClick={() => onCheckout(plan.lookup_key)}
        disabled={loading}
        data-testid={`paywall-checkout-${plan.lookup_key}`}
        className={`inline-flex w-full items-center justify-center gap-2 border px-3 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors disabled:opacity-60 ${
          featured
            ? "border-[#3b82f6] bg-[#3b82f6] text-white hover:bg-[#2563eb]"
            : "border-[#1e293b] bg-[#111827] text-[#f8fafc] hover:border-[#334155]"
        }`}
      >
        {loading ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Redirecting…
          </>
        ) : (
          <>
            {ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
          </>
        )}
      </button>
    </div>
  );
}

export default function Paywall() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ckLoading, setCkLoading] = useState(null); // lookup_key while redirecting
  const status = user?.subscription_status || "none";
  const copy = useStatusCopy(status);

  useEffect(() => {
    fetchPlans()
      .then((r) => setPlans(r.plans || []))
      .catch(() => toast.error("Couldn't load plans — try again in a moment."))
      .finally(() => setLoading(false));
  }, []);

  // If the user IS active/trialing, they don't belong on the paywall.
  useEffect(() => {
    if (user && ["trialing", "active"].includes(user.subscription_status)) {
      navigate("/feed", { replace: true });
    }
  }, [user, navigate]);

  const onCheckout = async (lookupKey) => {
    setCkLoading(lookupKey);
    try {
      const r = await startCheckout(lookupKey, window.location.origin);
      if (r.already_subscribed) {
        navigate("/feed", { replace: true });
        return;
      }
      if (r.checkout_url) {
        window.location.href = r.checkout_url;
        return;
      }
      toast.error("Checkout unavailable — try again shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Checkout failed.");
    } finally {
      setCkLoading(null);
    }
  };

  const onSignOut = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const monthly = plans.find((p) => p.lookup_key === "starter_monthly");
  const yearly = plans.find((p) => p.lookup_key === "starter_yearly");

  return (
    <div
      data-testid="paywall-page"
      className="min-h-screen bg-[#0b1120] px-6 py-10 text-[#f8fafc]"
    >
      <header className="mx-auto flex max-w-[1000px] items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center border border-[#3b82f6]/40 bg-[#3b82f6]/10">
            <Radar className="h-4 w-4 text-[#3b82f6]" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold tracking-tight">SIGNAL EDGE</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#64748b]">
              convergence intel
            </div>
          </div>
        </Link>
        {user && user !== false && (
          <div className="flex items-center gap-2">
            <span
              data-testid="paywall-user-chip"
              className="hidden max-w-[220px] truncate border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[10px] text-[#94a3b8] sm:inline"
            >
              {user.email}
            </span>
            <button
              type="button"
              onClick={onSignOut}
              data-testid="paywall-signout-button"
              className="border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc]"
            >
              Sign out
            </button>
          </div>
        )}
      </header>

      <main className="mx-auto mt-10 max-w-[1000px]">
        <div className="mx-auto max-w-xl text-center">
          <div className="inline-flex items-center gap-2 border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-[#3b82f6]">
            <Lock className="h-3 w-3" /> {copy.eyebrow}
          </div>
          <h1 className="mt-5 text-3xl font-bold tracking-tight text-[#f8fafc] sm:text-4xl">
            {copy.title}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-[#94a3b8]">{copy.body}</p>
        </div>

        {/* Feature strip */}
        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          <div className="flex items-start gap-3 border border-[#1e293b] bg-[#0f172a] p-4">
            <Zap className="mt-0.5 h-4 w-4 shrink-0 text-[#3b82f6]" />
            <div>
              <div className="text-sm font-semibold text-[#f8fafc]">Live convergence feed</div>
              <div className="mt-1 text-[11px] text-[#94a3b8]">1–5 signal scale, updates in real time.</div>
            </div>
          </div>
          <div className="flex items-start gap-3 border border-[#1e293b] bg-[#0f172a] p-4">
            <Shield className="mt-0.5 h-4 w-4 shrink-0 text-[#3b82f6]" />
            <div>
              <div className="text-sm font-semibold text-[#f8fafc]">Verified sources only</div>
              <div className="mt-1 text-[11px] text-[#94a3b8]">Every quote linked to a public recording or filing.</div>
            </div>
          </div>
          <div className="flex items-start gap-3 border border-[#1e293b] bg-[#0f172a] p-4">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#3b82f6]" />
            <div>
              <div className="text-sm font-semibold text-[#f8fafc]">Search by expert or ticker</div>
              <div className="mt-1 text-[11px] text-[#94a3b8]">Filter by any tracked voice or symbol.</div>
            </div>
          </div>
        </div>

        {loading ? (
          <div
            data-testid="paywall-loading"
            className="mt-8 flex items-center justify-center border border-[#1e293b] bg-[#111827] py-16 text-sm text-[#94a3b8]"
          >
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading plans…
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {monthly && (
              <PlanCard
                plan={monthly}
                ctaLabel={copy.ctaMonthly}
                onCheckout={onCheckout}
                loading={ckLoading === "starter_monthly"}
              />
            )}
            {yearly && (
              <PlanCard
                plan={yearly}
                featured
                ctaLabel={copy.ctaYearly}
                onCheckout={onCheckout}
                loading={ckLoading === "starter_yearly"}
              />
            )}
          </div>
        )}

        <div className="mt-8 text-center text-[11px] leading-relaxed text-[#64748b]">
          Signal Edge is a research tool. Nothing here is investment advice.
        </div>
      </main>
    </div>
  );
}
